'use client'

import { useBranchQRCode } from './useBranchQRCode'
import cx from 'classnames'
import styles from './styles.module.scss'

import { useDeviceOs } from '@mettle/hooks'
import { useEffect, useRef } from 'react'
import { performStoreRedirect } from './performStoreRedirect'
import { useExternalNavigation } from '@/hooks/useAnalytics'
import { Colour } from '@/components/constants/colours'
import { PanelItem } from '@/components/Panel/PanelItem'
import { CardCopy } from '@/components/Card/CardCopy/CardCopy'

const steps: { title: string; colour: Colour }[] = [
  {
    title: 'Scan the QR code to download Mettle',
    colour: Colour.teal100,
  },
  {
    title: 'Complete your application',
    colour: Colour.teal20,
  },
  {
    title: 'Start using Mettle',
    colour: Colour.softBlack,
  },
]

export interface QRCodeFormProps {
  title?: string
  instructions?: string
  isHashOpenAccount?: boolean
}

export const QRCodeForm = ({
  title = 'Apply in minutes from the app',
  instructions,
  isHashOpenAccount,
}: QRCodeFormProps) => {
  const { qrCode, loaded, type } = useBranchQRCode()
  const deviceOS = useDeviceOs()
  const logExternalNavigation = useExternalNavigation()
  const redirected = useRef(false)

  useEffect(() => {
    // Return early if user is just viewing QR code
    if (!isHashOpenAccount) return
    performStoreRedirect(
      deviceOS as string | undefined,
      redirected,
      logExternalNavigation,
    )
  }, [deviceOS, logExternalNavigation, isHashOpenAccount])

  const qrCodeSrc =
    typeof qrCode === 'string' ? qrCode : (qrCode as { src: string })?.src || ''

  return (
    <PanelItem className={styles.qrForm}>
      <div className={styles.applyCode}>
        <div className={cx({ [styles.titleContainer]: !!instructions })}>
          <h2 className={styles.title}>{title}</h2>
          {instructions && (
            <p className={styles.instructions}>{instructions}</p>
          )}
        </div>
        <img
          alt="Qr Code"
          className={cx(styles.qrCodeImage, {
            [styles.wobble]: loaded,
            [styles.staticQRCode]: type === 'static',
          })}
          src={qrCodeSrc}
        ></img>
      </div>

      <div className={styles.steps}>
        {steps.map((step, index) => (
          <div className={cx(styles.card, styles[step.colour])} key={index}>
            <CardCopy
              backgroundColour={step.colour}
              subtitle={step.title}
              number={String(index + 1)}
              reducedHeaders
            />
          </div>
        ))}
      </div>
    </PanelItem>
  )
}
